interface DocumentWithExecCommand extends Document {
  execCommand(commandId: string, showUI?: boolean, value?: string): boolean
}

const isMobile =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )

export async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch (error) {
      console.warn('Clipboard API failed, trying fallback:', error)
    }
  }

  // Fallback for mobile devices and older browsers
  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.setAttribute('aria-hidden', 'true')
    textarea.setAttribute('contenteditable', 'true')

    if (isMobile) {
      textarea.style.position = 'fixed'
      textarea.style.left = '0'
      textarea.style.top = '0'
      textarea.style.width = '1px'
      textarea.style.height = '1px'
      textarea.style.padding = '0'
      textarea.style.margin = '0'
      textarea.style.border = 'none'
      textarea.style.outline = 'none'
      textarea.style.boxShadow = 'none'
      textarea.style.background = 'transparent'
      textarea.style.opacity = '0'
      textarea.style.zIndex = '9999'
      textarea.style.pointerEvents = 'none'
    } else {
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      textarea.style.top = '0'
      textarea.style.width = '1px'
      textarea.style.height = '1px'
      textarea.style.padding = '0'
      textarea.style.border = 'none'
      textarea.style.outline = 'none'
      textarea.style.boxShadow = 'none'
      textarea.style.background = 'transparent'
      textarea.style.opacity = '0'
      textarea.style.zIndex = '-1'
    }

    document.body.appendChild(textarea)

    if (navigator.userAgent.match(/ipad|iphone/i)) {
      textarea.contentEditable = 'true'
      textarea.readOnly = false
      const range = document.createRange()
      range.selectNodeContents(textarea)
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
      textarea.setSelectionRange(0, text.length)

      await new Promise((resolve) => setTimeout(resolve, 100))
    } else {
      textarea.focus()
      textarea.select()
      if (isMobile) {
        textarea.setSelectionRange(0, text.length)
      }
    }

    let successful = false
    try {
      successful = (document as DocumentWithExecCommand).execCommand('copy')
    } catch (e) {
      console.warn('execCommand failed:', e)
    }

    if (!successful && isMobile) {
      try {
        const selection = window.getSelection()
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          const textToCopy = range.toString() || textarea.value

          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(textToCopy)
            successful = true
          }
        }
      } catch (e) {
        console.warn('Selection API fallback failed:', e)
      }
    }

    const selection = window.getSelection()
    if (selection) {
      selection.removeAllRanges()
    }
    document.body.removeChild(textarea)

    if (!successful) {
      throw new Error('execCommand copy failed')
    }
  } catch (error) {
    console.error('Failed to copy text:', error)
    throw error
  }
}
